import { useEffect, useRef, useState } from 'react'
import {
  faCheck,
  faChevronDown,
  faChevronUp,
  faCircleExclamation,
} from '@fortawesome/pro-solid-svg-icons'
import { Alert, Icon, Text } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'
import { PendingDots, StatusRow } from '../StatusRow'
import { MAX_WAIT_MS, pollDelayFor } from './polling'
import {
  fetchWaitStatus,
  POLL_SCHEDULE,
  toAccountDetail,
  toFinancialAccountIds,
  toHeadline,
  toHeadlineStatus,
  toOutcome,
  type WaitStatus,
} from './waitForReconciliation'
import type { WidgetProps } from './types'

const HEADLINE_ICON = {
  matched: { icon: faCheck, color: 'text-green-100', label: 'All matched' },
  attention: {
    icon: faCircleExclamation,
    color: 'text-orange-100',
    label: 'Something needs attention',
  },
}

export const CHECKING_LABEL = 'Checking your accounts against your bank'
export const STILL_CHECKING_LABEL =
  'Still checking your accounts against your bank'

export const WaitForReconciliationWidget = ({
  input,
  resolved,
  onOutcome,
}: WidgetProps) => {
  // TODO: use useWaitPoll, restartKey idsKey.
  // Never reset: a refused report reopens the card, and re-sending on every
  // poll would loop.
  const reported = useRef(false)
  // The host hands a fresh callback every render; keying the poll on it would
  // restart the wait each time the chat re-renders.
  const onOutcomeRef = useRef(onOutcome)
  onOutcomeRef.current = onOutcome
  const [status, setStatus] = useState<WaitStatus | null>(null)
  const [timedOut, setTimedOut] = useState(false)
  const [loadFailed, setLoadFailed] = useState(false)
  const [loadAttempt, setLoadAttempt] = useState(0)
  const [expanded, setExpanded] = useState(false)

  const idsKey = toFinancialAccountIds(input).join(',')

  useEffect(() => {
    const ids = idsKey ? idsKey.split(',').map(Number) : []
    const startedAt = Date.now()
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined

    const report = (next: WaitStatus) => {
      if (resolved || reported.current) return
      reported.current = true
      onOutcomeRef.current(toOutcome(next))
    }

    const poll = async () => {
      let next: WaitStatus
      try {
        next = await fetchWaitStatus(ids)
      } catch {
        if (!cancelled) setLoadFailed(true)
        return
      }
      if (cancelled) return
      setStatus(next)
      // A card reopened from history shows where things stand now, once.
      if (resolved) return
      if (!next.pending) {
        report(next)
        return
      }
      if (Date.now() - startedAt >= MAX_WAIT_MS) {
        setTimedOut(true)
        report(next)
        return
      }
      timer = setTimeout(
        () => void poll(),
        pollDelayFor(Date.now() - startedAt, POLL_SCHEDULE)
      )
    }

    setLoadFailed(false)
    void poll()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [idsKey, loadAttempt, resolved])

  if (loadFailed) {
    return (
      <Alert
        variant="error"
        description="Heard couldn't check on your accounts right now."
        ctaText="Try again"
        ctaOnClick={() => setLoadAttempt((attempt) => attempt + 1)}
      />
    )
  }

  if (!status || (status.pending && !timedOut && !resolved)) {
    return (
      <StatusRow label={CHECKING_LABEL} pending trailing={<PendingDots />} />
    )
  }

  if (status.pending) {
    return <StatusRow label={STILL_CHECKING_LABEL} pending trailing={null} />
  }

  const headlineStatus = toHeadlineStatus(status.accounts)
  const headlineIcon =
    headlineStatus === 'neutral' ? null : HEADLINE_ICON[headlineStatus]

  return (
    <StatusRow
      label={toHeadline(status.accounts)}
      pending={false}
      trailing={
        <>
          {headlineIcon && (
            <span role="img" aria-label={headlineIcon.label}>
              <Icon
                icon={headlineIcon.icon}
                color={headlineIcon.color}
                className="text-sm"
              />
            </span>
          )}
          <button
            type="button"
            aria-expanded={expanded}
            aria-label={expanded ? 'Hide accounts' : 'Show accounts'}
            className="text-neutral-400"
            onClick={() => setExpanded(!expanded)}
          >
            <Icon
              icon={expanded ? faChevronUp : faChevronDown}
              className="text-sm"
            />
          </button>
        </>
      }
    >
      {expanded && (
        <ul className="mt-2 flex flex-col pl-6">
          {status.accounts.map((account) => (
            <li
              key={account.financialAccountId}
              className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-t border-neutral-100 py-2 first:border-t-0"
            >
              <Text variant={TypographyStyles.BODY_MD}>
                {account.accountLabel}
              </Text>
              <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
                {toAccountDetail(account)}
              </Text>
            </li>
          ))}
        </ul>
      )}
    </StatusRow>
  )
}
